import { FormEvent, ReactNode } from "react";
import { Backdrop, CircularProgress, Container, Card, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LoadingButton from "./LoadingButton";
import ConfirmDialog from "../ConfirmDialog";
import { useIntl } from "../../contexts/IntlContext";

interface FormContainerProps {
  children: ReactNode;
  submitText: string;
  loading?: boolean;
  buttons?: ReactNode;
  redirectPath: string;
  onSubmit: (event: FormEvent, continueEditing?: boolean) => Promise<void>;
  continueEditing?: boolean;
  unsavedChangesBlocked?: boolean;
  onConfirmDiscardChanges?: () => void;
  onCancelDiscardChanges?: () => void;
}

export default function FormContainer({
  children,
  submitText,
  loading = false,
  buttons,
  redirectPath,
  onSubmit,
  continueEditing = false,
  unsavedChangesBlocked = false,
  onConfirmDiscardChanges,
  onCancelDiscardChanges,
}: FormContainerProps) {
  const intl = useIntl();
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent) => {
    //const target = (event.nativeEvent as any).submitter as HTMLButtonElement;
    //const continueEditing = target?.dataset?.action === 'continue';
    onSubmit(event, continueEditing);
  };
 
  return (
    <>
      <Backdrop open={loading} sx={{ color: "primary.main", zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="md" sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={3}
          sx={{
            width: '100%',
            p: 4,
            borderRadius: 2
          }}
        >
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {children}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: '0.5rem', '& > *': { width: { xs: '100%', sm: 'calc(50% - 0.25rem)', md: 'calc(33.33% - 0.34rem)' } } }}>
              {buttons}
              <LoadingButton
                fullWidth={false}
                disabled={loading}
                color="error"
                onClick={() => navigate(redirectPath)}
              >
                {intl.formatMessage({ id: 'common.cancel' })}
              </LoadingButton>
              <LoadingButton 
                type="submit" 
                fullWidth={false}
                loading={loading}
                color="primary"
              >
                {intl.formatMessage({ id: submitText })}
              </LoadingButton>
            </Box>
          </Box>
        </Card>
      </Container>
      {onConfirmDiscardChanges && onCancelDiscardChanges && (
        <ConfirmDialog
          open={unsavedChangesBlocked}
          title={intl.formatMessage({ id: "common.confirm" })}
          message={intl.formatMessage({ id: "messages.unsavedChanges" })}
          confirmLabel={intl.formatMessage({ id: "messages.leaveWithoutSaving" })}
          cancelLabel={intl.formatMessage({ id: "messages.stayOnPage" })}
          onConfirm={onConfirmDiscardChanges}
          onClose={onCancelDiscardChanges}
        />
      )}
    </>
  );
}